import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";

const API_URL = process.env.REACT_APP_API_URL;

const FILTER_KEY = "customerSalesOrderListFilters";


function parseJsonIfString(value) {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function parseApiResponse(raw) {
  const d = parseJsonIfString(raw);
  if (d?.body != null) return parseJsonIfString(d.body);
  return d;
}

function isApiSuccess(res) {
  if (!res || typeof res !== "object") return false;
  const s = res.success;
  return s === true || s === "true" || s === 1 || s === "1";
}

function extractOrdersPayload(res) {
  const block = parseJsonIfString(res?.data != null ? res.data : res);
  if (!block || typeof block !== "object") return { list: [], total: 0 };
  const raw = parseJsonIfString(block.salesOrders || block.orders || block.items || []);
  const list = Array.isArray(raw) ? raw : [];
  const total =
    typeof block.totalCount === "number" ? block.totalCount : list.length;
  return { list, total };
}

function formatDate(value) {
  if (!value) return "-";
  const str = String(value).trim();
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str || "-";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });
}

function getOrderId(o) {
  return o?._id || o?.id || "";
}

function toApiDDMMYYYY(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}-${mm}-${yyyy}`;
}


const CustomerSalesOrderList = () => {
  const saved = JSON.parse(sessionStorage.getItem(FILTER_KEY) || "{}");

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCountLoading, setIsCountLoading] = useState(true);
  const [activeCount, setActiveCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(saved.searchQuery || "");
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    saved.activeSearchQuery || "",
  );
  const [fromDate, setFromDate] = useState(saved.fromDate || "");
  const [toDate, setToDate] = useState(saved.toDate || "");
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [dateFilterChecked, setDateFilterChecked] = useState(
    Boolean(saved.fromDate || saved.toDate),
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(saved.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(saved.itemsPerPage || 25);
  const requestIdRef = useRef(0);
  const filterPanelRef = useRef(null);

  const hasActiveFilters = Boolean(activeSearchQuery || fromDate || toDate);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(
      FILTER_KEY,
      JSON.stringify({
        searchQuery,
        activeSearchQuery,
        fromDate,
        toDate,
        currentPage,
        itemsPerPage,
      }),
    );
  }, [searchQuery, activeSearchQuery, fromDate, toDate, currentPage, itemsPerPage]);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      setIsCountLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: "get-customer-sales-order-count",
          currentUserId: localStorage.getItem("id") || undefined,
        });
        const res = parseApiResponse(response.data);
        if (!cancelled && isApiSuccess(res)) {
          const data = parseJsonIfString(res.data);
          const d = data && typeof data === "object" ? data : {};
          setActiveCount(Number(d.activeTotalCount ?? d.totalCount) || 0);
        }
      } catch (error) {
        console.error("Customer sales order count error:", error);
      } finally {
        if (!cancelled) setIsCountLoading(false);
      }
    };
    fetchCount();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchOrders = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const payload = {
        action: "get-customer-sales-order",
        currentUserId: localStorage.getItem("id") || undefined,
        page: currentPage,
        limit: itemsPerPage,
      };
      const q = activeSearchQuery.trim();
      if (q) payload.search = q;
      if (fromDate) payload.fromDate = toApiDDMMYYYY(fromDate);
      if (toDate) payload.toDate = toApiDDMMYYYY(toDate);

      const response = await axios.post(API_URL, payload);
      if (requestId !== requestIdRef.current) return;

      const res = parseApiResponse(response.data);
      if (isApiSuccess(res)) {
        const { list, total } = extractOrdersPayload(res);
        setOrders(list);
        setTotalCount(total);
      } else {
        toast.error(res?.message || "Failed to fetch sales orders", {
          autoClose: 2000,
        });
        setOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Fetch customer sales orders error:", error);
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null;
      toast.error(
        errData?.message || "An error occurred while fetching sales orders",
        { autoClose: 2000 },
      );
      setOrders([]);
      setTotalCount(0);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, activeSearchQuery, fromDate, toDate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem = orders.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem =
    orders.length > 0 ? Math.min(currentPage * itemsPerPage, totalCount) : 0;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim() === "" && activeSearchQuery) {
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  };

  const handleSearchTrigger = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleFromDateChange = (e) => {
    const value = e.target.value;
    if (value && toDate && value > toDate) {
      toast.error("From date cannot be after To date", { autoClose: 2000 });
      return;
    }
    setFromDate(value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    const value = e.target.value;
    if (value && fromDate && value < fromDate) {
      toast.error("To date cannot be before From date", { autoClose: 2000 });
      return;
    }
    setToDate(value);
    setCurrentPage(1);
  };

  const handleDateFilterToggle = () => {
    if (dateFilterChecked) {
      setFromDate("");
      setToDate("");
      setCurrentPage(1);
    }
    setDateFilterChecked((prev) => !prev);
  };

  const handleClearDates = () => {
    setFromDate("");
    setToDate("");
    setDateFilterChecked(false);
    setIsFilterPanelOpen(false);
    setCurrentPage(1);
  };

  const handleClearAll = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    handleClearDates();
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value) || 25);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const max = 7;
    const tp = totalPages;
    if (tp <= max) {
      for (let i = 1; i <= tp; i++) pages.push(i);
    } else if (currentPage <= 4) {
      for (let i = 1; i <= 5; i++) pages.push(i);
      pages.push("ellipsis", tp);
    } else if (currentPage >= tp - 3) {
      pages.push(1, "ellipsis");
      for (let i = tp - 4; i <= tp; i++) pages.push(i);
    } else {
      pages.push(1, "ellipsis");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("ellipsis", tp);
    }
    return pages;
  };

  const labelStyle = { fontSize: "13px", fontWeight: "500" };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Sales Orders</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="procurement-stat-card-row rfq-stat-card-row">
                {isCountLoading ? (
                  <div className="procurement-stat-col">
                    <div className="card supplier-stat-card">
                      <div className="card-body">
                        <div className="stat-skeleton stat-skeleton-title" aria-hidden="true" />
                        <div className="stat-skeleton stat-skeleton-value" aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="procurement-stat-col">
                    <div className="card supplier-stat-card procurement-stat-card-active">
                      <div className="card-body">
                        <div className="stat-title" style={{ color: "#196F3D" }}>
                          Active
                        </div>
                        <div className="stat-value" style={{ color: "#27AE60" }}>
                          {activeCount}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="card-body">
                  <div className="row align-items-end mb-4">
                    <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2">
                      <div className="form-group mb-0">
                        <label className="mb-1" style={labelStyle}>
                          Search
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search sales orders..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchKeyPress}
                          />
                          <div className="input-group-append">
                            <div
                              className="input-group-text crsr_pntr"
                              onClick={handleSearchTrigger}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-search" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2">
                      <div
                        className="form-group mb-0 position-relative"
                        ref={filterPanelRef}
                      >
                        <label className="mb-1" style={labelStyle}>
                          Filter
                        </label>
                        <button
                          type="button"
                          className="form-control text-left d-flex justify-content-between align-items-center"
                          onClick={() => setIsFilterPanelOpen((open) => !open)}
                        >
                          <span
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {fromDate || toDate ? "Filters" : "Select Filters"}
                          </span>
                          <i
                            className={`fa-solid fa-chevron-${isFilterPanelOpen ? "up" : "down"}`}
                            style={{
                              fontSize: "11px",
                              flexShrink: 0,
                              marginLeft: "6px",
                            }}
                          />
                        </button>

                        {isFilterPanelOpen && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 20,
                              top: "100%",
                              left: 0,
                              marginTop: "4px",
                              background: "#fff",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              padding: "10px 12px",
                              width: "200px",
                              maxWidth: "calc(100vw - 32px)",
                              boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                            }}
                          >
                            <div className="custom-control custom-checkbox mb-2">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id="filterCheckDateRange"
                                checked={dateFilterChecked}
                                onChange={handleDateFilterToggle}
                              />
                              <label
                                className="custom-control-label"
                                htmlFor="filterCheckDateRange"
                              >
                                Date
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {dateFilterChecked && (
                      <>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2">
                          <div className="form-group mb-0">
                            <label className="mb-1" style={labelStyle}>
                              From Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={fromDate}
                              max={toDate || undefined}
                              onChange={handleFromDateChange}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2">
                          <div className="form-group mb-0">
                            <label className="mb-1" style={labelStyle}>
                              To Date
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={toDate}
                              min={fromDate || undefined}
                              onChange={handleToDateChange}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {(fromDate || toDate) && (
                      <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm w-100"
                          onClick={handleClearDates}
                          style={{ minWidth: "100px", whiteSpace: "nowrap" }}
                        >
                          <i className="fa-solid fa-times mr-1" />
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {isLoading && orders.length === 0 ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : orders.length === 0 && !hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  There are no sales orders yet.
                </div>
              ) : orders.length === 0 && hasActiveFilters ? (
                <div className="nocontent_div text-center p-4">
                  No sales orders found matching your filters.
                  <br />
                  <div className="mt-3">
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleClearAll}
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className="supply-table-container"
                  style={{ position: "relative" }}
                >
                  {isLoading && (
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.55)",
                        pointerEvents: "none",
                      }}
                    >
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  )}
                  <div
                    className="table-responsive"
                    style={{
                      opacity: isLoading ? 0.6 : 1,
                      transition: "opacity 0.15s ease",
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <table
                      id="customerSalesOrders"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: "nowrap", width: "40px", minWidth: "40px" }}>
                            S.No
                          </th>
                          <th style={{ minWidth: "120px", whiteSpace: "nowrap" }}>CPO No</th>
                          <th style={{ minWidth: "100px", whiteSpace: "nowrap" }}>CPO Date</th>
                          <th style={{ minWidth: "110px", whiteSpace: "nowrap" }}>Delivery Date</th>
                          <th style={{ minWidth: "180px" }}>Summary</th>
                          <th style={{ width: "80px", minWidth: "80px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o, index) => {
                          const oid = getOrderId(o);
                          return (
                            <tr key={oid || index}>
                              <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {o?.cpoNumber || "-"}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDate(o?.cpoDate)}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDate(o?.deliverDate)}
                              </td>
                              <td
                                style={{
                                  maxWidth: "240px",
                                  wordWrap: "break-word",
                                  whiteSpace: "normal",
                                }}
                              >
                                {String(o?.cpoSummary || "").trim() || "-"}
                              </td>
                              <td>
                                <div
                                  align="center"
                                  style={{ whiteSpace: "nowrap" }}
                                  className="d-flex justify-content-center align-items-center"
                                >
                                  <Link
                                    to={`${"/customerSalesOrderView"}/${oid}`}
                                    className="crsr_pntr"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div
                    className="supply-pagination-container"
                    style={{
                      opacity: isLoading ? 0.65 : 1,
                      pointerEvents: isLoading ? "none" : "auto",
                    }}
                  >
                    <div className="pagination-wrapper">
                      <div className="pagination-info">
                        <label className="mb-0 mr-2" style={{ fontSize: "13px" }}>
                          Show:
                        </label>
                        <select
                          className="form-control form-control-sm"
                          value={itemsPerPage}
                          onChange={handleItemsPerPageChange}
                        >
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                        <span className="ml-2" style={{ fontSize: "13px" }}>
                          entries
                        </span>
                        <span className="ml-3 text-muted" style={{ fontSize: "13px" }}>
                          {startItem}–{endItem} of {totalCount}
                        </span>
                      </div>

                      <nav className="pagination-nav">
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(1)}
                              disabled={currentPage === 1}
                              title="First Page"
                            >
                              <i className="fa-solid fa-angle-double-left" />
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              disabled={currentPage === 1}
                              title="Previous Page"
                            >
                              <i className="fa-solid fa-angle-left" />
                            </button>
                          </li>
                          {getPageNumbers().map((page, index) =>
                            page === "ellipsis" ? (
                              <li key={`ellipsis-${index}`} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            ) : (
                              <li
                                key={page}
                                className={`page-item ${currentPage === page ? "active" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ),
                          )}
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              title="Next Page"
                            >
                              <i className="fa-solid fa-angle-right" />
                            </button>
                          </li>
                          <li
                            className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(totalPages)}
                              disabled={currentPage === totalPages}
                              title="Last Page"
                            >
                              <i className="fa-solid fa-angle-double-right" />
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSalesOrderList;